<template>
  <div class="field-readiness-mobile">
    <!-- Header: Lot & Trade Info -->
    <header class="site-header">
      <div class="brand">
        <i class="fas fa-hard-hat"></i>
        <span>Praetorian AI Field</span>
      </div>
      <div class="lot-info">
        <h1>Lot {{ lotId }}</h1>
        <div class="trade-badge">{{ tradeType }}</div>
      </div>
    </header>

    <main class="content-area">
      <!-- Loading State -->
      <div v-if="loading" class="loading-overlay">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Analyzing lot readiness...</p>
      </div>

      <div v-else>
        <!-- 1. Critical Success List (The Learning Loop) -->
        <section class="checklist-section">
          <div class="section-header">
            <i class="fas fa-clipboard-check"></i>
            <h2>Critical Success List</h2>
          </div>
          <p class="section-subtitle">Based on historical failures and specific lot options.</p>
          
          <div class="checklist-items">
            <div 
              v-for="(item, index) in checklistItems" 
              :key="index" 
              class="check-item"
              :class="{ 'checked': item.completed }"
              @click="toggleItem(index)"
            >
              <div class="check-box">
                <i v-if="item.completed" class="fas fa-check"></i>
              </div>
              <div class="item-text">
                <p>{{ item.text }}</p>
                <span v-if="item.source" class="source-tag">{{ item.source }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- 2. High Risk Alerts (Municipal/Quality Logs) -->
        <section v-if="riskPatterns.length" class="risk-section">
          <div class="section-header alert">
            <i class="fas fa-exclamation-triangle"></i>
            <h2>Recurring Risk Alerts</h2>
          </div>
          <div class="risk-cards">
            <div v-for="(risk, index) in riskPatterns" :key="index" class="risk-card">
              <strong>{{ risk.title }}</strong>
              <p>{{ risk.description }}</p>
            </div>
          </div>
        </section>

        <!-- 3. Verification Action -->
        <section class="action-section">
          <button 
            @click="verifyReadiness" 
            class="verify-btn"
            :disabled="!allChecked || verifying"
          >
            <i :class="verifying ? 'fas fa-spinner fa-spin' : 'fas fa-shield-alt'"></i>
            {{ verifying ? 'Submitting...' : 'Verify & Signal Readiness' }}
          </button>
          <p class="action-note">This signals the superintendent and updates the ERP.</p>
        </section>
      </div>
    </main>

    <footer class="footer">
      <p>© 2026 Praetorian AI • Operational Readiness System</p>
    </footer>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

export default {
  name: 'FieldReadiness',
  setup() {
    const route = useRoute()
    const toast = useToast()
    
    const lotId = computed(() => route.params.lotId)
    const tradeType = computed(() => route.params.tradeType)
    
    const loading = ref(true)
    const verifying = ref(false)
    const checklistItems = ref([])
    const riskPatterns = ref([])

    const loadReadinessData = async () => {
      try {
        loading.value = true
        // In a real implementation, this endpoint would be public or token-authorized
        const response = await api.get(`/real-estate/field-readiness/${lotId.value}/${tradeType.value}/`)
        
        // Map the LLM generated checklist to our state
        // The backend RealEstateAgentService.get_field_learning_loop returns this
        checklistItems.value = response.data.checklist_items || []
        riskPatterns.value = response.data.risk_patterns || []
      } catch (error) {
        console.error('Error loading readiness data:', error)
        toast.error('Failed to load lot data. Please scan again.')
        
        // Mock data for demo purposes if API fails
        checklistItems.value = [
          { text: 'Verify Option #405: Upstairs Egress Windows installed', source: 'Buyer Option', completed: false },
          { text: 'Check framing headers for 10ft ceiling height spec', source: 'Trade SOW', completed: false },
          { text: 'Ensure fire-blocking is complete per Plan B revision', source: 'Historical Miss', completed: false }
        ]
        riskPatterns.value = [
          { title: 'Egress Clearance', description: 'Plan B consistently fails egress clearance in Bedroom 3. Double check rough opening.' }
        ]
      } finally {
        loading.value = false
      }
    }

    const toggleItem = (index) => {
      checklistItems.value[index].completed = !checklistItems.value[index].completed
    }

    const allChecked = computed(() => {
      return checklistItems.value.length > 0 && checklistItems.value.every(item => item.completed)
    })

    const verifyReadiness = async () => {
      try {
        verifying.value = true
        await api.post(`/real-estate/field-verify/`, {
          lot_id: lotId.value,
          trade: tradeType.value,
          verified_items: checklistItems.value
        })
        toast.success('Readiness signaled! You are good to go.')
      } catch (error) {
        toast.error('Submission failed. Check connection.')
      } finally {
        verifying.value = false
      }
    }

    onMounted(loadReadinessData)

    return {
      lotId,
      tradeType,
      loading,
      verifying,
      checklistItems,
      riskPatterns,
      toggleItem,
      allChecked,
      verifyReadiness
    }
  }
}
</script>

<style scoped>
.field-readiness-mobile {
  min-height: 100vh;
  background-color: #f8fafc;
  font-family: 'DM Sans', -apple-system, sans-serif;
  color: #1e293b;
  display: flex;
  flex-direction: column;
}

.site-header {
  background: #1e293b;
  color: white;
  padding: 1.5rem;
  border-bottom: 4px solid #3b82f6;
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 1rem;
}

.lot-info h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
}

.trade-badge {
  display: inline-block;
  background: #3b82f6;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-top: 0.5rem;
}

.content-area {
  flex: 1;
  padding: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.section-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.section-header i {
  color: #3b82f6;
}

.section-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 1.5rem;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.check-item {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s;
  cursor: pointer;
}

.check-item.checked {
  border-color: #10b981;
  background: #f0fdf4;
}

.check-box {
  width: 28px;
  height: 28px;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.checked .check-box {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.item-text p {
  margin: 0;
  font-weight: 600;
  line-height: 1.4;
}

.source-tag {
  font-size: 0.7rem;
  background: #f1f5f9;
  color: #64748b;
  padding: 0.125rem 0.4rem;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 700;
}

.risk-section {
  margin-top: 2rem;
}

.section-header.alert i {
  color: #ef4444;
}

.risk-cards {
  margin-top: 1rem;
}

.risk-card {
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  padding: 1rem;
  border-radius: 0 8px 8px 0;
}

.risk-card strong {
  display: block;
  color: #991b1b;
  margin-bottom: 0.25rem;
}

.risk-card p {
  margin: 0;
  font-size: 0.875rem;
  color: #b91c1c;
}

.action-section {
  margin-top: 2.5rem;
  text-align: center;
}

.verify-btn {
  width: 100%;
  background: #1e293b;
  color: white;
  border: none;
  padding: 1.25rem;
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.2s;
}

.verify-btn:not(:disabled):hover {
  background: #334155;
  transform: translateY(-2px);
}

.verify-btn:disabled {
  background: #94a3b8;
  opacity: 0.7;
}

.action-note {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.75rem;
}

.footer {
  padding: 2rem;
  text-align: center;
  font-size: 0.75rem;
  color: #94a3b8;
}

.loading-overlay {
  text-align: center;
  padding: 4rem 0;
  color: #64748b;
}

.loading-overlay i {
  font-size: 2rem;
  margin-bottom: 1rem;
}
</style>

